<script setup>
const { $twMerge } = useNuxtApp();
import { ref, nextTick, onMounted, onBeforeUnmount } from "vue";
import Splide from "@splidejs/splide";
import { AutoScroll } from "@splidejs/splide-extension-auto-scroll";
const splideRef = ref(null);
let splide = null;
let scrollObserver = null;

const props = defineProps({
  appendClass: {
    type: String,
    default: "",
    required: false,
  },
});

onMounted(async () => {
  if (import.meta.client) {
    await nextTick();
    if (splideRef.value) {
      splide = new Splide(
        splideRef.value,
        {
          perPage: 4,
          gap: "0px",
          breakpoints: {
            767: { perPage: 1 },
            1023: { perPage: 2 },
          },
          type: "loop",
          arrows: false,
          pagination: false,
          drag: false,
          autoScroll: {
            speed: 1,
            pauseOnHover: false,
            pauseOnFocus: false,
          },
        },
        {
          AutoScroll,
        }
      ).mount({ AutoScroll });

      // Pause the marquee while the section is off-screen: the extension's
      // rAF loop keeps translating the track even when nobody is looking,
      // so gate it with the same viewport pattern as the other sections.
      scrollObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          const autoScroll = splide?.Components.AutoScroll;
          if (!entry || !autoScroll) return;
          if (entry.isIntersecting) {
            autoScroll.play();
          } else {
            autoScroll.pause();
          }
        },
        { rootMargin: "100px 0px" },
      );
      scrollObserver.observe(splideRef.value);
    }
  }
});

onBeforeUnmount(() => {
  scrollObserver?.disconnect();
  scrollObserver = null;
  // Destroying the instance cancels the extension's rAF loop and listeners.
  // Without this, the marquee keeps running against a detached DOM after
  // navigating away from the page.
  splide?.destroy();
  splide = null;
});

// Plain array: nothing ever mutates this list, so a reactive proxy over
// 20 items was pure overhead
const aboutUsImgs = [
  { image: "/images/AboutImg4.webp", alt: "Woman speaking on stage at a Laravel event", appendClass: "" },
  { image: "/images/AboutImg1.webp", alt: "WebReinvent team celebrating together in front of a branded backdrop", appendClass: "md:mt-24" },
  { image: "/images/AboutImg2.webp", alt: "WebReinvent Instagram post celebrating its 14th Foundation Day", appendClass: "md:mt-[238px]" },
  { image: "/images/AboutImg3.webp", alt: "Colleagues at the office reception holding gift boxes", appendClass: "md:mt-[127px]" },
  { image: "/images/AboutImg5.webp", alt: "Team member speaking on stage at a tech event", appendClass: "md:mt-[163px]" },
  { image: "/images/AboutImg6.webp", alt: "Team in festive wear at an Independence Day celebration", appendClass: "md:mt-[100px]" },
  { image: "/images/AboutImg7.webp", alt: "Colleagues presenting a gift box at an office celebration", appendClass: "md:mt-[220px]" },
  { image: "/images/AboutImg8.webp", alt: "Speaker at the WebReinvent Foundation Day 2024 podium", appendClass: "md:mt-[155px]" },
  { image: "/images/AboutImg9.webp", alt: "Team celebrating Holi at the office", appendClass: "" },
  { image: "/images/AboutImg10.webp", alt: "Team member speaking at the ZEN DEV X conference stage", appendClass: "md:mt-[163px]" },
  { image: "/images/AboutImg11.webp", alt: "WebReinvent team celebrating together in front of a branded backdrop", appendClass: "md:mt-[90px]" },
  { image: "/images/AboutImg12.webp", alt: "Two colleagues dancing at an office celebration", appendClass: "md:mt-[210px]" },
  { image: "/images/AboutImg13.webp", alt: "Colleagues at the office reception holding gift boxes", appendClass: "md:mt-[105px]" },
  { image: "/images/AboutImg14.webp", alt: "Colleague holding a gift in front of a Happy New Year banner", appendClass: "" },
  { image: "/images/AboutImg15.webp", alt: "Portrait of a WebReinvent leader in a suit at the office", appendClass: "md:mt-[180px]" },
  { image: "/images/AboutImg16.webp", alt: "WebReinvent team celebrating together in front of a branded backdrop", appendClass: "md:mt-[110px]" },
  { image: "/images/AboutImg17.webp", alt: "Colleagues in festive wear at a Happy Diwali photo booth", appendClass: "md:mt-[230px]" },
  { image: "/images/AboutImg18.webp", alt: "Indian flag flying in front of the office building", appendClass: "md:mt-[140px]" },
  { image: "/images/AboutImg19.webp", alt: "Colleagues playfully lifting each other at an office celebration", appendClass: "" },
  { image: "/images/AboutImg20.webp", alt: "14th Foundation Day 2024 event backdrop", appendClass: "md:mt-[170px]" },
];
</script>

<template>
  <div
    class="max-w-screen-xl mx-auto relative z-10"
    :class="props.appendClass ? props.appendClass : 'xl:pt-10'"
  >
    <h2 class="pt-[54px] mb-2 max-md:mb-4 md:mb-4 text-center">
      <WrGradientText
        append-class="pb-0 pt-0 lg:pb-2 bg-clip-text bg-[linear-gradient(92.15deg,_var(--text-gradient-1)_35.57%,_var(--text-gradient-2)_55.72%)]"
        >Our Story in Pictures</WrGradientText
      >
      Moments that fuel our passion
    </h2>

    <div ref="splideRef" class="splide w-full h-full overflow-hidden">
      <div class="splide__track h-full">
        <ul class="splide__list">
          <li
            class="splide__slide !max-w-max max-md:!max-w-none"
            v-for="(card, index) in aboutUsImgs"
            :key="index"
          >
            <div
              :class="$twMerge(
                'min-w-max max-md:min-w-0 max-md:mx-auto',
                card.appendClass
              )"
            >
              <NuxtImg
                :src="card.image"
                :alt="card.alt"
                height="487"
                width="299"
                loading="lazy"
                decoding="async"
                format="webp"
              />
            </div>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped></style>
